// QR Kiosk v2 — KV2-6 track B draft-ticket artifact (SHIPS DARK, gated by
// KIOSK_DRAFT_TICKET_ENABLED = false). Inline stream card (designer ArtifactCard)
// + fullscreen artifact stage (draft review, photo add, amber not-saved callout,
// Create ticket, Back to chat) + created state. Create fires ONLY on the button
// tap → reuses the existing createServiceReport POST with a ROUTE-SCOPED
// machineNumber (never bot-supplied, never automatic — PG-17). §3-T8.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Machine } from "../../models/machinesModels";
import { ChangeServiceReport, ServiceReportFileTypeCode } from "../../models/serviceReportsModels";
import { createServiceReport, getMachineParts, getServiceReports } from "../../publicSlice";
import { midasSessionMarkCreated } from "../kioskV2Slice";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { ErrorCallout } from "../shared/ErrorCallout";
import { KDate, UrgencyTag, MetaCell } from "../reports/ReportBits";
import { REPORT_DATE_FORMAT, findPartTranslationKey } from "../reports/reportUtils";
import { FileTileGrid } from "../reports/FileTileGrid";
import { useKioskFiles } from "../reports/useKioskFiles";
import { KioskDraftTicket, validateDraftForCreate } from "./draftTicketContract";

const REPORTS_QUERY = { statusCodes: ["InProgress", "Solved"] };
const EM_DASH = "—";

export interface DraftTicketArtifactV2Props {
  machine: Machine;
  draft?: KioskDraftTicket;
  messageId: string;
  created: boolean;
  card?: boolean; // inline stream card
  stage?: boolean; // fullscreen artifact stage
  onOpen?: () => void; // card → open the stage
  onBack?: () => void; // stage → back to chat
}

const miniRow = (label: string, value: React.ReactNode) => (
  <div key={label} style={{ display: "flex", gap: 10, fontSize: 11.5 }}>
    <span style={{ width: 60, flexShrink: 0, color: "var(--faint)", fontFamily: "var(--font-mono)", fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.06em", paddingTop: 1 }}>{label}</span>
    <span style={{ color: "var(--ink-2)", fontWeight: 500, minWidth: 0 }}>{value}</span>
  </div>
);

export function DraftTicketArtifactV2({ machine, draft, messageId, created, card, stage, onOpen, onBack }: DraftTicketArtifactV2Props) {
  const { translate } = useTranslation(["public", "servicing"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const machineParts = useAppSelector((s) => s.public.machineParts);
  const { files, addFiles, removeFile, rejected, dismissRejected } = useKioskFiles(ServiceReportFileTypeCode.reportDocument);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const openPress = usePressed();
  const createPress = usePressed();

  // Load the part taxonomy when the stage opens (PartPicker pattern) — needed for
  // validation + the part-label display.
  useEffect(() => {
    if (stage) dispatch(getMachineParts());
  }, [stage, dispatch]);

  if (!draft) return null;

  const partKey = findPartTranslationKey(machineParts, draft.affectedPartCategoryId);
  const partLabel = partKey ? translate(`servicing:machineParts.parts.${partKey}`) : draft.affectedPartLabel || EM_DASH;
  const validation = validateDraftForCreate(draft, machineParts);

  const handleCreate = async () => {
    if (submitting || !validation.ok) return; // one explicit tap; double-tap guarded
    setSubmitting(true);
    setSubmitError(undefined);
    const parsed = draft.openDate ? new Date(draft.openDate) : null;
    const openDate = parsed && parsed <= new Date() ? parsed : moment().toDate();
    const model: ChangeServiceReport = {
      title: draft.title,
      description: draft.description || "",
      notes: draft.notes,
      alarmCode: draft.alarmCode,
      urgency: draft.urgency,
      openDate,
      affectedPartCategoryId: draft.affectedPartCategoryId,
      files,
    };
    try {
      const res = await dispatch(createServiceReport({ model, machineNumber: machine.internalNumber }));
      if (createServiceReport.rejected.match(res)) {
        setSubmitError(translate("kioskV2MidasCreateFailed"));
        return;
      }
      await dispatch(getServiceReports({ machineNumber: machine.internalNumber, query: REPORTS_QUERY }));
      dispatch(midasSessionMarkCreated({ messageId, atIso: new Date().toISOString() }));
      onBack?.();
    } catch {
      setSubmitError(translate("kioskV2MidasCreateFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Inline stream card (designer ArtifactCard) ──
  if (card) {
    return (
      <div style={{ marginTop: 10, border: "1px solid var(--midas-line)", borderRadius: 12, background: "var(--card)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", background: "var(--midas-soft)", borderBottom: "1px solid var(--midas-line)" }}>
          <span style={{ color: "var(--midas)", display: "inline-flex" }}>
            <Glyph d={created ? GD.check : GD.clip} size={16} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)" }}>{translate("kioskV2MidasDraftCardTitle")}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--muted)", letterSpacing: "0.05em" }}>
              {translate(created ? "kioskV2MidasDraftCreatedCardLabel" : "kioskV2MidasDraftCardSub")}
            </div>
          </div>
          {created ? (
            <button type="button" onClick={() => navigate(`/public-v2/machines/${machine.internalNumber}/service-reports/open`)} style={pillStyle}>
              {translate("kioskV2MidasViewReports")}
            </button>
          ) : (
            <button type="button" {...openPress.pressHandlers} onClick={onOpen} style={{ ...pillStyle, ...openPress.pressStyle }}>
              {translate("kioskV2MidasDraftOpen")} ↗
            </button>
          )}
        </div>
        <div style={{ padding: "9px 12px", display: "flex", flexDirection: "column", gap: 5 }}>
          {miniRow(translate("kioskV2WhatsWrong"), draft.title)}
          {miniRow(translate("affectedMachinePart"), partLabel)}
          {miniRow(translate("urgency"), <UrgencyTag urgency={draft.urgency} />)}
        </div>
      </div>
    );
  }

  // ── Fullscreen artifact stage (designer MidasFull stage="artifact") ──
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, height: 54, padding: "0 12px", background: "var(--card)", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <button type="button" aria-label={translate("kioskV2MidasBackToChat")} onClick={onBack} style={{ width: 36, height: 36, borderRadius: 10, border: "1px solid var(--border)", background: "var(--card)", display: "grid", placeItems: "center", color: "var(--ink-2)", cursor: "pointer" }}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
            <path d="M6 6 L18 18" />
            <path d="M18 6 L6 18" />
          </svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{translate("kioskV2MidasDraftCardTitle")}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--faint)", letterSpacing: "0.05em" }}>{translate("kioskV2MidasDraftTopSub")}</div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: "12px 14px" }}>
        <div style={{ border: "1px solid var(--border)", borderRadius: 12, background: "var(--card)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
          <div style={{ padding: "12px 16px 4px" }}>
            <div className="eyebrow" style={{ color: "var(--midas)", marginBottom: 6 }}>{translate("kioskV2MidasDraftEyebrow")}</div>
            <div style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: "-0.01em", color: "var(--ink)", lineHeight: 1.3 }}>{draft.title}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 14, padding: "4px 16px 6px" }}>
            <MetaCell label={translate("machine")} value={`${translate("serialNumber")} ${machine.serialNumber}`} />
            <MetaCell label={translate("openDate")} value={<KDate date={draft.openDate || moment().toDate()} format={REPORT_DATE_FORMAT} />} />
            <MetaCell label={translate("affectedMachinePart")} value={partLabel} mono={false} />
            <MetaCell label={translate("urgency")} value={<UrgencyTag urgency={draft.urgency} />} mono={false} />
          </div>
          <div style={{ padding: "8px 16px 14px", borderTop: "1px solid var(--border-soft)" }}>
            <div className="eyebrow" style={{ color: "var(--faint)", marginBottom: 5 }}>{translate("kioskV2MidasDraftFromChat")}</div>
            {draft.description && <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55, marginBottom: 10 }}>{draft.description}</div>}
            <FileTileGrid files={files} onAdd={addFiles} onRemove={removeFile} rejected={rejected} onDismissRejected={dismissRejected} variant="photo" inputId="kv2-draft-files" />
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, padding: "9px 12px", borderRadius: 10, background: "color-mix(in oklab, var(--amber) 12%, var(--card))", border: "1px solid color-mix(in oklab, var(--amber) 36%, var(--border))" }}>
          <span style={{ color: "var(--amber)", fontSize: 13 }}>⚠</span>
          <span style={{ flex: 1, fontSize: 11.5, color: "var(--ink-2)", lineHeight: 1.4 }}>{translate("kioskV2MidasNothingSaved")}</span>
        </div>

        {submitError && <div style={{ marginTop: 10 }}><ErrorCallout title={submitError} /></div>}
        {!validation.ok && (
          <div style={{ marginTop: 8, fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--faint)", letterSpacing: "0.03em" }}>{translate("kioskV2MidasDraftIncomplete")}</div>
        )}
      </div>

      <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 9, padding: "12px 14px calc(14px + env(safe-area-inset-bottom))", background: "var(--card)", borderTop: "1px solid var(--border)" }}>
        <button
          type="button"
          {...createPress.pressHandlers}
          disabled={submitting || !validation.ok}
          onClick={handleCreate}
          style={{ height: 54, borderRadius: 12, border: "none", background: "var(--midas)", color: "var(--tier-accent-fg)", fontFamily: "inherit", fontSize: 15.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 9, cursor: submitting || !validation.ok ? "default" : "pointer", opacity: submitting || !validation.ok ? 0.6 : 1, ...createPress.pressStyle }}
        >
          <Glyph d={GD.check} size={18} />
          {translate("kioskV2MidasCreateTicket")}
        </button>
        <button type="button" onClick={onBack} style={{ height: 44, borderRadius: 11, border: "1px solid var(--border)", background: "var(--bg)", color: "var(--ink-2)", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
          {translate("kioskV2MidasBackToChat")}
        </button>
      </div>
    </>
  );
}

const pillStyle: React.CSSProperties = {
  height: 26,
  padding: "0 10px",
  borderRadius: 8,
  border: "1px solid var(--midas)",
  background: "var(--midas)",
  color: "var(--tier-accent-fg)",
  fontFamily: "inherit",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
  flexShrink: 0,
};

export default DraftTicketArtifactV2;
